import determineTarget from './determine-target';
import setupFocusTrap from './focus-trap';
import toggleElement from './toggle-element';

const modalTriggers = new WeakMap();

export default function setupModals( endpoints ) {
	const focusTraps = new WeakMap();

	// Initialize all dialog elements
	document.querySelectorAll( '.js-modal' ).forEach( modal => {
		modal.tabIndex = -1;
		modal.setAttribute( 'aria-hidden', true );

		if ( modal.getAttribute( 'role' ) === 'dialog' ) {
			const toggleTrap = setupFocusTrap( modal );
			focusTraps.set( modal, toggleTrap );
		}
	} );

	function setuModalReset( name, value ) {
		const mediaQuery = window.matchMedia( value );

		function resetModals() {
			const modals = document.querySelectorAll( `.js-${ name }-modal` );

			if ( mediaQuery.matches ) {
				modals.forEach( modal => {
					modal.removeAttribute( 'tabindex' );
					modal.removeAttribute( 'role' );
					modal.setAttribute( 'aria-hidden', false );
					focusTraps.get( modal )?.( false );
				} );
			} else {
				modals.forEach( modal => {
					modal.tabIndex = -1;
					modal.setAttribute( 'role', 'dialog' );
					modal.setAttribute( 'aria-hidden', true );
					focusTraps.get( modal )?.( true );
				} );
			}
		}

		resetModals();
		if ( typeof mediaQuery.addEventListener === 'undefined' ) {
			mediaQuery.addListener( resetModals );
		} else {
			mediaQuery.addEventListener( 'change', resetModals );
		}

		return mediaQuery;
	}

	const modalEndpoints = endpoints.map( ( { name, value } ) => {
		return {
			name,
			mediaQuery: setuModalReset( name, value ),
		};
	} );

	// Handle opening the modal from a button that controls it
	document.addEventListener( 'click', event => {
		const toggle = event.target.closest( '[aria-controls], [data-target]' );

		// Skip if it doesn't control anything
		if ( ! toggle || ( ! toggle.hasAttribute( 'aria-controls' ) && ! toggle.dataset.target ) ) {
			return;
		}

		// Skip if the target isn't a dialog
		const target = determineTarget( toggle );
		if ( ! target || ! target.classList.contains( 'js-modal' ) ) {
			return;
		}

		// Check breakpoints
		const endpointMatch = modalEndpoints.find( ( { name, mediaQuery } ) => {
			return toggle.classList.contains( `js-${ name }-modal` ) && mediaQuery.matches;
		} );
		if ( endpointMatch ) {
			return;
		}

		event.preventDefault();

		// Show if hidden or a stackable modal, otherwise toggle
		const show = target.getAttribute( 'aria-hidden' ) === 'true' || target.classList.contains( 'js-modal--stackable' );

		// Open and focus the dialog
		toggleElement( target, show, 'focus' );

		// Reset scroll position
		target.scrollTop = 0;

		// Record the trigger for refocusing later
		modalTriggers.set( target, toggle );
	} );

	// Handle closing the modal, by clicking the close button or backdrop
	document.addEventListener( 'click', ( { target } ) => {
		if ( ! target.classList.contains( 'js-modal-close' ) && ! target.classList.contains( 'js-modal-backdrop' ) ) {
			return;
		}

		// Find the parent dialog
		const modal = target.closest( '.js-modal' );
		if ( ! modal ) {
			return;
		}

		// Close the dialog
		toggleElement( modal, false );

		// Refocus the trigger if found
		if ( modalTriggers.has( modal ) ) {
			modalTriggers.get( modal ).focus();
		}
	} );

	// Handle closing the modal via ESC key
	document.addEventListener( 'keyup', ( { key, target } ) => {
		if ( key !== 'Escape' ) {
			return;
		}

		// Find the parent dialog
		const modal = target.closest( '.js-modal' );
		if ( ! modal ) {
			return;
		}

		// Close the dialog
		toggleElement( modal, false );

		// Refocus the trigger if found
		if ( modalTriggers.has( modal ) ) {
			modalTriggers.get( modal ).focus();
		}
	} );
}
